import { useCallback, useEffect, useMemo, useState } from 'react';
import { sortByJersey } from '../gameUtils';
import { statsGameApi } from '../statsGameApi';

const DEFAULT_CLOCK = { quarter_length_sec: 600, running: false, started_at: null, remaining_sec: 600 };

// Loads box score, on-court lineups, recent plays and clock; re-polls every 6s for multi-device sync.
export function useGameData(gameId) {
  const [game, setGame] = useState(null);
  const [boxscore, setBoxscore] = useState(null);
  const [teamTotals, setTeamTotals] = useState(null);
  const [recentEntries, setRecentEntries] = useState([]);
  const [onCourt, setOnCourt] = useState({ a: [], b: [] });
  const [quarter, setQuarter] = useState(1);
  const [clock, setClock] = useState(DEFAULT_CLOCK);
  const [videoUrl, setVideoUrl] = useState('');
  const [loading, setLoading] = useState(true);

  const fetchAll = useCallback(async () => {
    try {
      const [boxRes, courtRes, recentRes, clockRes] = await Promise.all([
        statsGameApi.boxscore(gameId),
        statsGameApi.onCourt(gameId),
        statsGameApi.recentEntries(gameId),
        statsGameApi.clock(gameId),
      ]);
      if (boxRes.ok) {
        const d = await boxRes.json();
        setGame(d.game);
        setBoxscore(d.boxscore);
        setTeamTotals(d.team_totals);
        setQuarter(d.game.quarter || 1);
        if (d.game.video_url) setVideoUrl(d.game.video_url);
      }
      if (courtRes.ok) setOnCourt(await courtRes.json());
      if (recentRes.ok) setRecentEntries(await recentRes.json());
      if (clockRes.ok) setClock(await clockRes.json());
    } catch (e) { /* ignore */ }
    finally { setLoading(false); }
  }, [gameId]);

  useEffect(() => {
    fetchAll();
    const id = setInterval(fetchAll, 6000);
    return () => clearInterval(id);
  }, [fetchAll]);

  return {
    game, boxscore, teamTotals, recentEntries, onCourt, loading, fetchAll,
    quarter, setQuarter, clock, setClock, videoUrl, setVideoUrl,
  };
}

// Jersey-sorted full roster, on-court and bench players per team.
export function useCourtRosters(game, onCourt) {
  const playersByTeam = useMemo(() => ({
    a: [...(game?.team_a?.players || [])].sort(sortByJersey),
    b: [...(game?.team_b?.players || [])].sort(sortByJersey),
  }), [game]);

  const onCourtPlayers = useMemo(() => ({
    a: playersByTeam.a.filter(p => onCourt.a.includes(p.id)),
    b: playersByTeam.b.filter(p => onCourt.b.includes(p.id)),
  }), [playersByTeam, onCourt]);

  const benchPlayers = useMemo(() => ({
    a: playersByTeam.a.filter(p => !onCourt.a.includes(p.id)),
    b: playersByTeam.b.filter(p => !onCourt.b.includes(p.id)),
  }), [playersByTeam, onCourt]);

  return { playersByTeam, onCourtPlayers, benchPlayers };
}
